import { useEffect, useState, type ReactNode } from 'react';
import { Link, useLocation } from 'react-router';
import {
  ArrowLeft,
  BarChart3,
  Bell,
  Building2,
  Calendar,
  ChevronDown,
  ChevronRight,
  ClipboardList,
  Eye,
  FileText,
  KeyRound,
  LayoutDashboard,
  LogOut,
  MessageSquareText,
  Plus,
  Rocket,
  ShieldCheck,
  SlidersHorizontal,
  Sparkles,
  Upload,
  UserRound,
  Users,
} from 'lucide-react';
import { useAdminAuth } from '../hooks/useAdminAuth';
import { adminEventService, type AdminEvent } from '../services/adminEventService';
import { SAFETY_REPORTS_UPDATED_EVENT, safetyService } from '../services/safetyService';
import {
  EVENT_WORKSPACE_UPDATED_EVENT,
  enabledEventContentTypes,
  type EventContentType,
} from '../utils/eventWorkspace';

const navItems = [
  { label: 'Dashboard', href: '/dashboard', icon: LayoutDashboard },
  { label: 'Events', href: '/events', icon: Calendar },
  { label: 'AI Event Builder', href: '/ai-event-builder', icon: Sparkles },
];

type EventGroupKey = 'content' | 'experience' | 'people' | 'engagement';

const contentIcons: Record<EventContentType['icon'], typeof Calendar> = {
  calendar: Calendar,
  user: UserRound,
  file: FileText,
  building: Building2,
  users: Users,
};

function activeGroup(pathname: string): EventGroupKey | null {
  if (/\/(content(?:\/setup)?|sessions|speakers|papers|partners|committee)$/.test(pathname)) return 'content';
  if (/\/(app-setup|interests)$/.test(pathname)) return 'experience';
  if (/\/(attendees|invite-codes|access)$/.test(pathname)) return 'people';
  if (/\/feedback$/.test(pathname) || pathname === '/notifications') return 'engagement';
  return null;
}

function EventLink({
  href,
  label,
  icon: Icon,
  active,
  topLevel = false,
}: {
  href: string;
  label: string;
  icon: typeof Calendar;
  active: boolean;
  topLevel?: boolean;
}) {
  const { pathname, hash } = useLocation();
  return (
    <Link
      to={href}
      onClick={() => {
        if (href === `${pathname}${hash}` && hash) {
          document.getElementById(hash.slice(1))?.scrollIntoView({ block: 'start' });
        }
      }}
      aria-current={active ? 'page' : undefined}
      className={`${topLevel
        ? 'flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm transition-colors'
        : 'ml-3 flex items-center gap-2.5 rounded-lg px-3 py-2 text-xs transition-colors'
      } ${
        active
          ? 'bg-sky-500/10 font-medium text-sky-300'
          : `${topLevel ? 'text-gray-300' : 'text-gray-400'} hover:bg-gray-800 hover:text-gray-100`
      }`}
    >
      <Icon size={topLevel ? 16 : 14} />
      <span className="truncate">{label}</span>
    </Link>
  );
}

function EventGroup({
  label,
  icon: Icon,
  expanded,
  onToggle,
  active,
  children,
}: {
  label: string;
  icon: typeof Calendar;
  expanded: boolean;
  onToggle: () => void;
  active: boolean;
  children: ReactNode;
}) {
  return (
    <div>
      <button
        type="button"
        aria-expanded={expanded}
        onClick={onToggle}
        className={`flex w-full items-center gap-3 rounded-lg px-3 py-2 text-left text-sm transition-colors ${
          active ? 'bg-sky-500/10 font-medium text-sky-300' : 'text-gray-300 hover:bg-gray-800'
        }`}
      >
        <Icon size={16} />
        <span className="flex-1">{label}</span>
        <ChevronDown size={14} className={`transition-transform ${expanded ? 'rotate-180' : ''}`} />
      </button>
      {expanded && <div className="mt-0.5 space-y-0.5">{children}</div>}
    </div>
  );
}

export default function AdminLayout({ children }: { children: ReactNode }) {
  const { pathname, search } = useLocation();
  const pathEventId = pathname.match(/^\/events\/([^/]+)(?:\/|$)/)?.[1];
  const searchParams = new URLSearchParams(search);
  const queryEventId = searchParams.get('event_id') ?? undefined;
  const eventId = pathEventId ?? (['/notifications', '/analytics'].includes(pathname) ? queryEventId : undefined);
  const {
    user,
    signOut,
    organizerContext,
    activeWorkspaceId,
    setActiveWorkspaceId,
    isPlatformAdmin,
    canCreateEvents,
    activeWorkspace,
  } = useAdminAuth();
  const [event, setEvent] = useState<AdminEvent | null>(null);
  const [openSafetyCount, setOpenSafetyCount] = useState(0);
  const [expanded, setExpanded] = useState<Record<EventGroupKey, boolean>>({
    content: false,
    experience: false,
    people: false,
    engagement: false,
  });
  const routeGroup = activeGroup(pathname);
  const isEventSubPage = !!eventId;
  const canReviewEventSafety = isPlatformAdmin || (event?.id === eventId && event?.effective_role === 'admin');
  const canManageEvent = event?.id === eventId && (event?.effective_role === 'admin' || event?.effective_role === 'manager');

  useEffect(() => {
    if (!canReviewEventSafety && !isPlatformAdmin) {
      setOpenSafetyCount(0);
      return;
    }
    let active = true;
    let requestVersion = 0;
    setOpenSafetyCount(0);
    const refresh = () => {
      const version = ++requestVersion;
      safetyService.pendingCount(eventId)
        .then(({ count }) => {
          if (active && version === requestVersion) setOpenSafetyCount(count);
        })
        .catch(() => { if (active && version === requestVersion) setOpenSafetyCount(0); });
    };
    refresh();
    window.addEventListener(SAFETY_REPORTS_UPDATED_EVENT, refresh);
    return () => {
      active = false;
      window.removeEventListener(SAFETY_REPORTS_UPDATED_EVENT, refresh);
    };
  }, [eventId, canReviewEventSafety, isPlatformAdmin, pathname]);

  useEffect(() => {
    if (!eventId) {
      setEvent(null);
      return;
    }
    let active = true;
    setEvent(null);
    adminEventService.get(eventId)
      .then((loaded) => {
        if (active) setEvent(loaded);
      })
      .catch(() => {
        if (active) setEvent(null);
      });
    return () => { active = false; };
  }, [eventId]);

  useEffect(() => {
    if (!routeGroup) return;
    setExpanded((current) => ({ ...current, [routeGroup]: true }));
  }, [routeGroup]);

  useEffect(() => {
    if (!eventId) return undefined;
    const handleWorkspaceUpdate = (updateEvent: Event) => {
      const updated = (updateEvent as CustomEvent<AdminEvent>).detail;
      if (updated?.id === eventId) setEvent(updated);
    };
    window.addEventListener(EVENT_WORKSPACE_UPDATED_EVENT, handleWorkspaceUpdate);
    return () => window.removeEventListener(EVENT_WORKSPACE_UPDATED_EVENT, handleWorkspaceUpdate);
  }, [eventId]);

  const toggleGroup = (key: EventGroupKey) => {
    setExpanded((current) => ({ ...current, [key]: !current[key] }));
  };

  const isActive = (href: string) => {
    const [hrefPath, hrefQuery = ''] = href.split('?');
    if (pathname !== hrefPath || !hrefQuery) return pathname === hrefPath;
    const expectedParams = new URLSearchParams(hrefQuery);
    return Array.from(expectedParams.entries()).every(([key, value]) => searchParams.get(key) === value);
  };

  const attendeeBaseUrl = import.meta.env.VITE_ATTENDEE_URL || (import.meta.env.DEV ? 'http://localhost:5173' : '');
  const contentTypes = event ? enabledEventContentTypes(event) : [];

  return (
    <div className="flex h-screen bg-gray-950 text-gray-100">
      <aside className="flex w-64 flex-shrink-0 flex-col border-r border-gray-800">
        <div className="border-b border-gray-800 px-6 py-5">
          <div className="flex items-center gap-2">
            <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-sky-500">
              <LayoutDashboard size={14} className="text-white" />
            </div>
            <span className="text-sm font-semibold text-white">Rubikoo Admin</span>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto">
          <nav aria-label="Primary navigation" className="space-y-0.5 px-3 py-4">
            {navItems
              .filter((item) => item.href !== '/ai-event-builder' || canCreateEvents)
              .map(({ label, href, icon: Icon }) => {
                const active = pathname === href || (href !== '/events' && pathname.startsWith(`${href}/`));
                const eventsActive = href === '/events' && isEventSubPage;
                return (
                  <Link
                    key={href}
                    to={href}
                    aria-current={active || eventsActive ? 'page' : undefined}
                    className={`flex items-center gap-3 rounded-lg px-3 py-2 text-sm transition-colors ${
                      active || eventsActive
                        ? 'bg-sky-500/10 font-medium text-sky-400'
                        : 'text-gray-400 hover:bg-gray-800 hover:text-gray-100'
                    }`}
                  >
                    <Icon size={16} />
                    {label}
                    {(active || eventsActive) && <ChevronRight size={14} className="ml-auto" />}
                  </Link>
                );
              })}

            <Link to="/workspaces" className={`flex items-center gap-3 rounded-lg px-3 py-2 text-sm transition-colors ${pathname.startsWith('/workspaces') ? 'bg-sky-500/10 font-medium text-sky-400' : 'text-gray-400 hover:bg-gray-800 hover:text-gray-100'}`}>
              <Building2 size={16} />Workspaces
            </Link>
            {isPlatformAdmin && (
              <Link to="/platform/workspaces" className={`flex items-center gap-3 rounded-lg px-3 py-2 text-sm transition-colors ${pathname.startsWith('/platform/workspaces') ? 'bg-sky-500/10 font-medium text-sky-400' : 'text-gray-400 hover:bg-gray-800 hover:text-gray-100'}`}>
                <ShieldCheck size={16} />Platform workspaces
              </Link>
            )}
            {isPlatformAdmin && (
              <Link to="/platform/safety" className={`flex items-center gap-3 rounded-lg px-3 py-2 text-sm transition-colors ${pathname === '/platform/safety' ? 'bg-sky-500/10 font-medium text-sky-400' : 'text-gray-400 hover:bg-gray-800 hover:text-gray-100'}`}>
                <ShieldCheck size={16} />Platform safety{!eventId && openSafetyCount > 0 ? ` (${openSafetyCount})` : ''}
              </Link>
            )}
          </nav>

          {isEventSubPage && eventId && (
            <nav aria-label="Event workspace" className="border-t border-gray-800 px-3 pb-5 pt-4">
              <div className="mb-4 rounded-xl border border-sky-500/20 bg-sky-500/5 p-3">
                <Link to="/events" className="inline-flex items-center gap-1 text-[11px] text-gray-400 hover:text-white">
                  <ArrowLeft size={12} /> All events
                </Link>
                <p className="mt-2 truncate text-sm font-semibold text-white">{event?.name ?? 'Event workspace'}</p>
                <div className="mt-1 flex items-center justify-between gap-2">
                  <span className="text-[11px] capitalize text-gray-400">{event?.status ?? 'Loading'}</span>
                  {attendeeBaseUrl && (
                    <a
                      href={`${attendeeBaseUrl}/event-preview/${eventId}`}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-1 text-[11px] font-medium text-sky-400 hover:text-sky-300"
                    >
                      <Eye size={12} /> Preview
                    </a>
                  )}
                </div>
              </div>

              <p className="px-3 pb-1 text-[10px] font-semibold uppercase tracking-wider text-gray-600">Set up</p>
              <div className="space-y-0.5">
                <EventLink
                  href={`/events/${eventId}/overview`}
                  label="Overview"
                  icon={ClipboardList}
                  active={pathname === `/events/${eventId}/overview`}
                  topLevel
                />

                <EventGroup
                  label="Content & schedule"
                  icon={Calendar}
                  expanded={expanded.content}
                  onToggle={() => toggleGroup('content')}
                  active={routeGroup === 'content'}
                >
                  <EventLink href={`/events/${eventId}/content`} label="Content overview" icon={ClipboardList} active={pathname === `/events/${eventId}/content`} />
                  {canManageEvent && (event?.effective_content_ingestion_enabled || event?.effective_role === 'admin') && <EventLink href={`/events/${eventId}/content/setup`} label="Bulk upload & review" icon={Upload} active={pathname === `/events/${eventId}/content/setup`} />}
                  {contentTypes.map((type) => {
                    const Icon = contentIcons[type.icon];
                    const href = type.path(eventId);
                    return <EventLink key={type.key} href={href} label={type.label} icon={Icon} active={isActive(href)} />;
                  })}
                  {canManageEvent && <EventLink href={`/events/${eventId}/content#add-content-type`} label="Add content type" icon={Plus} active={false} />}
                </EventGroup>

                <EventGroup
                  label="Attendee experience"
                  icon={SlidersHorizontal}
                  expanded={expanded.experience}
                  onToggle={() => toggleGroup('experience')}
                  active={routeGroup === 'experience'}
                >
                  <EventLink
                    href={`/events/${eventId}/app-setup?section=capabilities`}
                    label="Capabilities"
                    icon={SlidersHorizontal}
                    active={pathname === `/events/${eventId}/app-setup` && searchParams.get('section') !== 'home'}
                  />
                  <EventLink
                    href={`/events/${eventId}/app-setup?section=home`}
                    label="Home Page"
                    icon={LayoutDashboard}
                    active={isActive(`/events/${eventId}/app-setup?section=home`)}
                  />
                  <EventLink href={`/events/${eventId}/interests`} label="Interests" icon={UserRound} active={pathname === `/events/${eventId}/interests`} />
                </EventGroup>

                <EventGroup
                  label="People & access"
                  icon={Users}
                  expanded={expanded.people}
                  onToggle={() => toggleGroup('people')}
                  active={routeGroup === 'people'}
                >
                  <EventLink href={`/events/${eventId}/attendees`} label="Attendees" icon={Users} active={pathname === `/events/${eventId}/attendees`} />
                  <EventLink href={`/events/${eventId}/invite-codes`} label="Invitations" icon={KeyRound} active={pathname === `/events/${eventId}/invite-codes`} />
                  <EventLink href={`/events/${eventId}/access`} label="Access rules" icon={ShieldCheck} active={pathname === `/events/${eventId}/access`} />
                </EventGroup>

                <EventLink
                  href={`/events/${eventId}/publish`}
                  label="Review & publish"
                  icon={Rocket}
                  active={pathname === `/events/${eventId}/publish`}
                  topLevel
                />
              </div>

              <p className="mt-4 px-3 pb-1 text-[10px] font-semibold uppercase tracking-wider text-gray-600">Manage</p>
              <div className="space-y-0.5">
                <EventGroup
                  label="Engagement"
                  icon={MessageSquareText}
                  expanded={expanded.engagement}
                  onToggle={() => toggleGroup('engagement')}
                  active={routeGroup === 'engagement'}
                >
                  <EventLink href={`/notifications?event_id=${eventId}`} label="Notifications" icon={Bell} active={isActive(`/notifications?event_id=${eventId}`)} />
                  <EventLink href={`/events/${eventId}/feedback`} label="Feedback" icon={MessageSquareText} active={pathname === `/events/${eventId}/feedback`} />
                </EventGroup>
                <EventLink href={`/analytics?event_id=${eventId}`} label="Analytics" icon={BarChart3} active={isActive(`/analytics?event_id=${eventId}`)} topLevel />
                {canReviewEventSafety && <EventLink
                  href={`/events/${eventId}/safety`}
                  label={`Safety reports${openSafetyCount > 0 ? ` (${openSafetyCount})` : ''}`}
                  icon={ShieldCheck}
                  active={pathname === `/events/${eventId}/safety`}
                  topLevel
                />}
              </div>
            </nav>
          )}
        </div>

        <div className="border-t border-gray-800 px-3 py-3">
          {(organizerContext?.workspaces?.length ?? 0) > 0 && (
            <select
              aria-label="Active workspace"
              value={activeWorkspaceId ?? ''}
              onChange={(changeEvent) => setActiveWorkspaceId(changeEvent.target.value || null)}
              className="mb-3 w-full rounded-lg border border-gray-700 bg-gray-900 px-2 py-2 text-xs text-gray-300"
            >
              <option value="">Choose workspace</option>
              {organizerContext?.workspaces.map((workspace) => (
                <option key={workspace.id} value={workspace.id}>{workspace.name} · {workspace.status}</option>
              ))}
            </select>
          )}
          <div className="mb-1 flex items-center gap-3 px-3 py-2">
            <div className="flex h-7 w-7 items-center justify-center rounded-full bg-gray-700 text-xs font-medium text-gray-300">
              {user?.email?.[0]?.toUpperCase() ?? 'A'}
            </div>
            <div className="min-w-0 flex-1">
              <p className="truncate text-xs font-medium text-gray-200">{user?.email}</p>
              <p className="text-xs text-gray-500">{isPlatformAdmin ? 'Platform admin' : activeWorkspace?.role === 'admin' ? 'Workspace admin' : 'Workspace member'}</p>
            </div>
          </div>
          <button
            onClick={signOut}
            className="flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm text-gray-400 transition-colors hover:bg-gray-800 hover:text-red-400"
          >
            <LogOut size={16} />
            Sign out
          </button>
        </div>
      </aside>

      <main className="flex-1 overflow-y-auto">{children}</main>
    </div>
  );
}
